import { useMemo, useCallback } from 'react';
import { CLIP_UPDATE_TYPE } from '../../clipUpdateDataUtils/constants';
import { updateClipDataWithSplits } from '../../clipUpdateDataUtilsWithSplits/updateWithSplits';
import { VIDEO_SEGMENT_MAX_FILE_SIZE } from '../../constants';
import { getPlayerTimeFromSegmentTime } from '../../timeUtils';
import { getEndForSegment } from '@/player-config/src/utils/clipUtils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import useBrandKitComponent from '@/Pages/BrandKit/components/useBrandKitComponent';
import { getBrandKitUrl } from '@/Pages/BrandKit/components/utils';
import { showErrorToast } from '@/libs/toast/toast';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { Segment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/Segment';
import { SegmentType } from '@/player-config/src/types/Segment/Type';
import { VideoSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/VideoSegment';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';

const REMOVED_BRAND_KIT_NAME = 'placeholder';

export function useAddSegmentUpload(previousSegment: Segment | null) {
  const { clipId, clipData } = useClipsContext();
  const { seekTo } = useRemotionPlayerSeek();
  const { brandKitStore, clipsBrandKitStore, onFilesUpload, deleteItem } = useBrandKitComponent({
    key: 'intro_outro',
    title: 'Video',
    clipId: clipData.id,
    maxFileSize: VIDEO_SEGMENT_MAX_FILE_SIZE
  });

  const brandKitIntroOutro = useMemo(() => {
    return brandKitStore.intro_outro.map(video => getBrandKitUrl(video));
  }, [brandKitStore.intro_outro]);

  const clipBrandKitIntroOutro = useMemo(() => {
    return clipsBrandKitStore[clipId]?.intro_outro;
  }, [clipsBrandKitStore, clipId]);

  const clipIntroOutro = useMemo(() => {
    function shouldAddCurrentIntroOutroToList(
      currentIntroOutro: string | undefined,
      introOutroPreviewList: { url: string; name: string }[]
    ): boolean {
      return (
        !!currentIntroOutro &&
        currentIntroOutro.includes('brand_kit') &&
        !brandKitIntroOutro.includes(currentIntroOutro) &&
        !introOutroPreviewList.find(video => video.url === currentIntroOutro)
      );
    }

    const introOutroPreviewList = clipBrandKitIntroOutro
      ? clipBrandKitIntroOutro?.map(video => ({
          name: video,
          url: getBrandKitUrl(video, clipData.id)
        }))
      : [];
    const currentIntro = clipData.asset_metadata.intro;
    if (shouldAddCurrentIntroOutroToList(currentIntro, introOutroPreviewList)) {
      introOutroPreviewList.push({ url: currentIntro!, name: REMOVED_BRAND_KIT_NAME });
    }
    const currentOutro = clipData.asset_metadata.outro;
    if (shouldAddCurrentIntroOutroToList(currentOutro, introOutroPreviewList)) {
      introOutroPreviewList.push({ url: currentOutro!, name: REMOVED_BRAND_KIT_NAME });
    }
    return introOutroPreviewList;
  }, [
    clipBrandKitIntroOutro,
    clipData.asset_metadata.intro,
    clipData.asset_metadata.outro,
    clipData.id,
    brandKitIntroOutro
  ]);

  function uploadFiles(files: File[]) {
    onFilesUpload(files).catch(() =>
      showErrorToast(
        `Oops! Uploading your video encountered an issue. Please make sure your file is in MP4 format and doesn't exceed ${VIDEO_SEGMENT_MAX_FILE_SIZE}MB, then give it another go.`
      )
    );
  }

  const handleVideoSelect = useCallback(
    (url: string, duration: number) => {
      if (!previousSegment) {
        return;
      }

      let insertFrame = 0;
      if (previousSegment.type === SegmentType.VIDEO) {
        const videoSegment = previousSegment as VideoSegment;
        insertFrame = videoSegment.currentBounds?.toFrame || videoSegment.maxBounds?.toFrame || 0;
      } else if (previousSegment.type === SegmentType.COMPOSITE) {
        const compositeSegment = previousSegment as CompositeSegment;
        insertFrame = compositeSegment.currentBounds?.toFrame || compositeSegment.maxBounds?.toFrame || 0;
      }

      const newClipData = updateClipDataWithSplits({
        clipId,
        actionType: CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK,
        type: SegmentType.VIDEO,
        id: null,
        url,
        duration,
        afterSegmentId: previousSegment.id,
        insertFrame
      });

      const newPlayerTime = getPlayerTimeFromSegmentTime(
        getEndForSegment({ clip: newClipData, segmentId: previousSegment.id, isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations] }),
        newClipData,
        previousSegment.id
      );
      seekTo(newPlayerTime);
    },
    [previousSegment, clipId, seekTo]
  );

  return {
    clipIntroOutro,
    brandKitIntroOutro,
    brandKitStore,
    uploadFiles,
    onFilesUpload,
    handleVideoSelect,
    deleteItem,
    removedBrandKitName: REMOVED_BRAND_KIT_NAME
  };
}
